import { useState } from "react";
import { C } from "@bd/content";
import { money, type Company, type Review } from "@bd/sim";
import { Icon, Sheet, Why } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { cover } from "../ui/Hud";
import { IconVal, short } from "./Products";
import { t, tx } from "../i18n";

export function scoreColor(s: number): string {
  return s >= 8 ? "var(--green)" : s >= 6.5 ? "var(--teal)" : s >= 5 ? "var(--warn)" : "var(--bad)";
}

export function ScoreBadge({ score, label, compact }: { score: number; label?: string; compact?: boolean }) {
  // colour plus a word, never colour alone
  const word = score >= 8 ? t("Hit") : score >= 6.5 ? t("Good") : score >= 5 ? t("Mixed") : t("Flop");
  return (
    <span className="row" style={{ gap: 6 }}>
      <span className="score" style={{ background: scoreColor(score) }} aria-label={`${label ?? t("Score")} ${score.toFixed(1)}, ${word}`}>{score.toFixed(1)}</span>
      {!compact && <span className="muted" style={{ fontSize: "0.8rem" }}>{word}</span>}
    </span>
  );
}

const MAST_COLORS = ["#c0563f", "#2f6f8f", "#6b4c9a", "#3f7f4f", "#b07a2a", "#8a3d5c", "#2d5a7a", "#5a6b2f"];

/** A small masthead for each outlet: its initials on its own colour and shape, so reviewers have faces. */
export function Masthead({ id, size = 34 }: { id: string; size?: number }) {
  const name = C.reviewer[id]?.name ?? id;
  const initials = name.replace(/^The /, "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  // each outlet its own colour and shape: its place in the list, so no two share one
  const h = Math.max(0, Object.keys(C.reviewer).indexOf(id));
  const round = h % 3 === 0 ? "50%" : h % 3 === 1 ? "6px" : "2px 12px";
  return (
    <span aria-hidden className="display" style={{ width: size, height: size, flex: "none", display: "inline-grid", placeItems: "center", borderRadius: round, background: MAST_COLORS[h % MAST_COLORS.length], color: "#fbf6ea", fontSize: size * 0.42, letterSpacing: "0.02em", boxShadow: "inset 0 -3px 0 rgba(0,0,0,0.18)" }}>{initials}</span>
  );
}

/** The reasons in a breakdown come from the rules engine in English: the fixed ones, and a few simple shapes, translate. */
const FIXED_REASONS = new Set([
  "Loves that nobody has made this combo before.", "Nothing new, nothing copied.", "Hates how much the dice decide.", "Likes that skill wins.",
  "Easy to learn.", "A bit hard to learn.", "Plenty of game for the money.", "The price is higher than the box feels worth.",
  "A cover that pops.", "A bland cover.", "Rich lore.", "Thin lore.", "No problems left.",
  "The marketing promised more than the game delivers.", "Nobody has made this pairing before.", "Kept inside the scale.",
]);
function reasonText(why: string): string {
  if (FIXED_REASONS.has(why)) return t(why);
  let m: RegExpExecArray | null;
  if ((m = /^Has seen (\d+) games like this lately\.$/.exec(why))) return t("Has seen {n} games like this lately.", { n: m[1] });
  if ((m = /^About (\d+) minutes: too long for a school night\.$/.exec(why))) return t("About {n} minutes: too long for a school night.", { n: m[1] });
  if ((m = /^Critics in (\d+) have played a lot of games: the bar is higher than it was\.$/.exec(why))) return t("Critics in {year} have played a lot of games: the bar is higher than it was.", { year: m[1] });
  return why;
}

function Breakdown({ r }: { r: Review }) {
  if (!r.lines.length) return <p className="muted" style={{ fontSize: "0.85rem", margin: "6px 0 0" }}>{t("Reviews over five years old keep only the score and quote; your 30 best games keep everything.")}</p>;
  return (
    <div className="rv-break" data-testid="breakdown">
      {r.lines.map((l) => (
        <div key={l.key} className="rv-line">
          <strong>{l.key === "taste" ? t("{name}'s taste", { name: C.reviewer[r.reviewer]?.name ?? r.reviewer }) : t(l.label)}</strong>
          <span className="rv-reason">{reasonText(l.why)}</span>
          <span className="num rv-pts" style={{ color: l.points < 0 ? "var(--bad)" : undefined }}>{l.points >= 0 ? "+" : ""}{l.points.toFixed(1)}</span>
        </div>
      ))}
      <div className="rv-line rv-total">
        <strong>{t("Score")}</strong>
        <span className="rv-reason" />
        <span className="num rv-pts">{r.score.toFixed(1)}</span>
      </div>
    </div>
  );
}

export function Reviews({ productId }: { productId: string }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const co = me(game) as Company;
  const p = co.products.find((x) => x.id === productId);
  const pinned = co.products.filter((x) => x.launched).indexOf(p!) < 5; // the first five releases open their breakdowns
  // early on, the first reviewer's breakdown is open to show how scores work; the rest open on demand
  const [open, setOpen] = useState<Record<string, boolean>>(() => (pinned && p?.reviews[0] ? { [p.reviews[0].reviewer]: true } : {}));
  if (!p) return null;
  const d = p.design;
  const concept = `${t(C.mechanic[d.mechanic].name)} × ${t(C.theme[d.theme].name)} · ${t(C.audience[d.audience].name)}`;
  return (
    <Sheet title={d.name} onClose={() => set({ screen: null })} wide testId="reviews">
      <div className="rv-head">
        <img className="rv-cover" src={cover(d.name, d.theme, d.mechanic, co.color, p.q.art, 2, false, d.audience)} width={192} height={144} alt={concept} title={concept} />
        <div className="stack rv-sum">
          <span className="rv-score"><ScoreBadge score={p.score} label={t("Average")} /></span>
          {p.perfectCombo && <span className="tag good" title={t("Secret perfect combo found: {combo}!", { combo: p.perfectCombo })}><Icon name="star" size={11} /> {p.perfectCombo}</span>}
          <span className="ivals">
            <IconVal icon="coin" label={`${t("Price {price}", { price: money(d.price) })} · ${p.channels.map((c) => t(C.channel[c].name)).join(", ")}`}>{money(d.price)}</IconVal>
            <IconVal glyph="sold" label={t("Sold: {n}", { n: p.sold.toLocaleString("en-US") })}>{short(p.sold)}</IconVal>
            <IconVal icon="box" label={t("in stock: {n}", { n: p.stock.toLocaleString("en-US") })}>{short(p.stock)}</IconVal>
          </span>
        </div>
        <div className="rv-bars">
          {(["F", "M", "P", "V", "T"] as const).map((k, i) => (
            <div key={k} className="rv-bar">
              <span>{t({ F: "Fit", M: "Focus", P: "Polish", V: "Value", T: "Trend" }[k])}</span>
              <span className="meter"><i style={{ width: `${p.inputs[k] * 100}%`, background: "var(--teal)" }} /></span>
              <span className="num">{Math.round(p.inputs[k] * 100)}%</span>
              {i === 0 ? <Why title={t("What reviewers looked at")} lines={[
                { label: t("Fit: mechanic, theme and audience match"), value: `${Math.round(p.inputs.F * 100)}%` },
                { label: t("Focus: the mix players want"), value: `${Math.round(p.inputs.M * 100)}%` },
                { label: t("Polish: problems fixed in playtests"), value: `${Math.round(p.inputs.P * 100)}%` },
                { label: t("Value: worth the price"), value: `${Math.round(p.inputs.V * 100)}%` },
                { label: t("Trend: good timing"), value: `${Math.round(p.inputs.T * 100)}%` },
                ...(p.inputs.H > 0.05 ? [{ label: t("Overhyped"), value: `−${p.inputs.H.toFixed(1)}` }] : []),
              ]} note={t("Fit counts most, trend least. Each reviewer also has their own taste.")} /> : <span />}
            </div>
          ))}
        </div>
      </div>
      <div className="stack" style={{ gap: 6 }}>
        {p.reviews.map((r) => (
          <div key={r.reviewer} className="card rv-card">
            <div className="rv-row">
              <Masthead id={r.reviewer} />
              <div className="rv-who">
                <strong>{C.reviewer[r.reviewer].name}</strong>
                <div className="muted">"{tx(r.quote)}"</div>
              </div>
              <ScoreBadge score={r.score} compact />
              {r.lines.length > 0 ? (
                <button className="icon-btn rv-why" onClick={() => setOpen((o) => ({ ...o, [r.reviewer]: !o[r.reviewer] }))} aria-expanded={!!open[r.reviewer]} aria-label={t("Why?")} title={t("Why?")} data-testid="why-review">
                  <Icon name="chevron" size={18} style={{ transform: open[r.reviewer] ? "rotate(90deg)" : undefined, transition: "transform 120ms" }} />
                </button>
              ) : <span className="rv-why" />}
            </div>
            {open[r.reviewer] && r.lines.length > 0 && <Breakdown r={r} />}
          </div>
        ))}
      </div>
    </Sheet>
  );
}
